/**
 * A folha de aprovação de pedido de compra, que o navegador salva como PDF.
 *
 * Papel: desenha a folha que vai ao aprovador — com um pedido, a folha
 * detalhada (título "Pedido de compra — solicitação de aprovação", número e
 * casa, solicitante, data, canal e fornecedor, a tabela de itens com foto, o
 * bloco de ajustes, a observação e o "TOTAL DO PEDIDO"); com dois ou mais, a
 * folha em lote, um bloco compacto por pedido e o "TOTAL GERAL" — e, assim que
 * monta, abre a janela de impressão com ela.
 *
 * Como usar: montar a folha é o que imprime, e por isso quem a monta só o faz
 * no pedido de impressão, com uma `key` nova a cada pedido, e a desmonta quando
 * `onConcluida` é chamado — no `afterprint`, ou 3 s depois de a janela fechar
 * quando o navegador não dispara o evento. Os pedidos chegam com as fotos já
 * em mãos, por `buscarFotosDaFolha`. Enquanto a folha está montada, quem a monta
 * deverá esconder da impressão todo o resto da página (`print:hidden`), senão o
 * papel sai com a tela e a folha depois dela.
 *
 * Montada, a folha troca o `document.title` pelo `nomeDoArquivoDaFolha`, que é
 * o nome que a janela de impressão sugere ao salvar o PDF, e o devolve ao
 * desmontar.
 *
 * Na tela a folha nunca aparece (`hidden`): ela só existe para a mídia de
 * impressão (`print:block`), na página nomeada `folha-retrato` de `index.css`.
 * O `<main>` da moldura tem 32 px de respiro nas laterais, e a folha os desfaz
 * com `print:-mx-8` e `print:w-[calc(100%+4rem)]`; se o respiro do `<main>`
 * mudar, as duas compensações terão de mudar junto.
 */
import { useEffect } from 'react';

import { useImpressaoAoMontar } from '@/shared/hooks';
import { formatarData, formatarDinheiro } from '@/shared/lib/formato';
import { cn } from '@/shared/lib/utils';
import {
  type AjustesDoPedido,
  type ItemNaFolha,
  type PedidoNaFolha,
  ajustesDoPedido,
  diaEHoraDaGeracao,
  fornecedorDaFolha,
  nomeDoArquivoDaFolha,
  notaDeAjusteDoItem,
  totalDaFolha,
} from '../folha-de-aprovacao';

/** Quanto esperar pelo `afterprint` antes de desmontar a folha assim mesmo. */
const LIMITE_DA_IMPRESSAO_EM_MS = 3000;

/** O minus das linhas de desconto, o `−` (U+2212). */
const MENOS = '−';

/**
 * O `th` da tabela de itens. Sem quebra de linha: "Valor unit." quebrado em
 * duas dobra a altura do cabeçalho e estreita a coluna dos valores.
 */
const CLASSES_DO_TH =
  'border-b border-linha px-[18px] py-2.5 text-left font-mono text-[10px] font-semibold tracking-[0.08em] whitespace-nowrap text-texto-fraco uppercase';

/** O `tbody` da tabela de itens: a linha de baixo e o respiro de cada `td`. */
const CLASSES_DO_CORPO =
  'font-sans text-[12.5px] [&_td]:border-b [&_td]:border-linha [&_td]:align-top [&_tr:last-child_td]:border-b-0';

/** O respiro de cada `td` na folha detalhada. */
const CLASSES_DO_CORPO_DETALHADO = '[&_td]:px-[18px] [&_td]:py-[11px]';

/** O respiro e a letra de cada `td` no lote, mais apertados. */
const CLASSES_DO_CORPO_COMPACTO = '[&_td]:px-3.5 [&_td]:py-1.5 [&_td]:text-[11.5px]';

/**
 * A célula numérica, sem quebra de linha: com ela, a tabela espremida separa o
 * "R$" do número e põe cada um numa linha. É a descrição que cede a largura, e
 * só ela quebra.
 */
const CLASSES_DO_NUMERO = 'text-right font-mono whitespace-nowrap';

/** A linha de texto miúdo sob a descrição do item: fornecedor ou ajuste. */
const CLASSES_DA_NOTA = 'mt-0.5 text-[11px] text-texto-fraco';

interface FolhaDeAprovacaoProps {
  /** Um pedido para a folha detalhada, dois ou mais para a folha em lote. */
  pedidos: readonly PedidoNaFolha[];
  /** O instante do pedido de impressão, em ISO 8601 com fuso. */
  geradaEm: string;
  /** Chamado quando a janela de impressão fecha, impressa ou cancelada. */
  onConcluida: () => void;
}

/**
 * A folha de aprovação, que abre a janela de impressão ao montar.
 *
 * @param pedidos - Os pedidos, com as fotos já buscadas.
 * @param geradaEm - O instante do pedido; dá o dia do nome do arquivo e a hora
 *   do "gerado às" do lote, no horário de Brasília.
 * @param onConcluida - Chamado uma vez, quando a janela fecha.
 * @returns A folha, escondida na tela e visível só na impressão.
 */
export function FolhaDeAprovacao({ pedidos, geradaEm, onConcluida }: FolhaDeAprovacaoProps) {
  const { dia, hora } = diaEHoraDaGeracao(geradaEm);
  const nomeDoArquivo = nomeDoArquivoDaFolha(pedidos, dia);

  // Antes de `useImpressaoAoMontar`: o título precisa estar trocado quando a
  // janela de impressão abrir, porque é dele que ela tira o nome do arquivo.
  useEffect(() => {
    const tituloDaPagina = document.title;
    document.title = nomeDoArquivo;
    return () => {
      document.title = tituloDaPagina;
    };
  }, [nomeDoArquivo]);

  const refDaFolha = useImpressaoAoMontar<HTMLDivElement>(
    onConcluida,
    LIMITE_DA_IMPRESSAO_EM_MS,
  );
  const [unico] = pedidos;

  return (
    <div
      ref={refDaFolha}
      className="hidden bg-superficie p-[20mm_18mm] font-sans text-foreground [page:folha-retrato] [print-color-adjust:exact] print:-mx-8 print:block print:w-[calc(100%+4rem)]"
    >
      {pedidos.length === 1 && unico !== undefined ? (
        <FolhaDetalhada pedido={unico} />
      ) : (
        <FolhaEmLote pedidos={pedidos} hora={hora} />
      )}
    </div>
  );
}

/**
 * O cabeçalho da folha: o título serifado e a linha mono em latão.
 *
 * @param titulo - O título.
 * @param subtitulo - A linha abaixo dele; sai em caixa alta.
 * @returns O cabeçalho com o filete de baixo.
 */
function CabecalhoDaFolha({ titulo, subtitulo }: { titulo: string; subtitulo: string }) {
  return (
    <div className="mb-7 border-b border-linha-forte pb-[18px]">
      <h1 className="mb-1.5 font-serif text-[23px] font-medium text-foreground">{titulo}</h1>
      <p className="font-mono text-[10.5px] tracking-[0.08em] text-brass uppercase">
        {subtitulo}
      </p>
    </div>
  );
}

/**
 * A faixa do total, com o rótulo mono à esquerda e o valor serifado à direita.
 *
 * @param rotulo - `TOTAL DO PEDIDO` ou `TOTAL GERAL (N pedidos)`.
 * @param valor - O total líquido.
 * @returns A faixa com o fundo latão claro.
 */
function FaixaDoTotal({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <div className="mb-6 flex items-center justify-between rounded-md border border-linha-forte bg-brass/8 px-[18px] py-3.5 font-mono text-[13px] tracking-[0.04em] text-texto-medio">
      <span>{rotulo}</span>
      <strong className="font-serif text-[20px] font-medium tracking-normal text-brass-light">
        {formatarDinheiro(valor)}
      </strong>
    </div>
  );
}

/**
 * O bloco "Subtotal dos itens / Descontos / Acréscimos (frete/taxas)".
 *
 * @param ajustes - As somas de `ajustesDoPedido`.
 * @param compacto - `true` no lote, com margem e letra menores.
 * @returns O bloco, sob o filete tracejado.
 */
function BlocoDeAjustes({
  ajustes,
  compacto,
}: {
  ajustes: AjustesDoPedido;
  compacto: boolean;
}) {
  const linhas: [string, string][] = [
    ['Subtotal dos itens', formatarDinheiro(ajustes.subtotal)],
  ];
  if (ajustes.descontos > 0) {
    linhas.push(['Descontos', `${MENOS} ${formatarDinheiro(ajustes.descontos)}`]);
  }
  if (ajustes.acrescimos > 0) {
    linhas.push(['Acréscimos (frete/taxas)', `+ ${formatarDinheiro(ajustes.acrescimos)}`]);
  }

  return (
    <div
      className={cn(
        'border-t border-dashed border-linha-forte pt-1.5',
        compacto ? 'mx-3.5 mt-1.5 mb-2 text-[11.5px]' : 'mx-[18px] mt-2.5 mb-3 text-[12.5px]',
      )}
    >
      {linhas.map(([rotulo, valor]) => (
        <div key={rotulo} className="flex justify-between gap-4 py-0.5">
          <span>{rotulo}</span>
          <span className="font-mono">{valor}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * A célula da foto do item; vazia quando o item não tem imagem.
 *
 * @param item - O item da linha.
 * @param compacto - `true` no lote, com a miniatura de 56 px no lugar da de 68.
 * @returns A célula.
 */
function CelulaDaFoto({ item, compacto }: { item: ItemNaFolha; compacto: boolean }) {
  return (
    <td className={compacto ? 'w-16' : 'w-[76px]'}>
      {item.foto !== null && (
        <img
          src={item.foto}
          alt="Foto do item"
          className={cn(
            // Sem o max-w-none, o max-width:100% do preflight prende a foto à
            // largura da célula, que o padding já come, e ela sai espremida.
            'block max-w-none rounded-sm border border-linha-forte object-cover',
            compacto ? 'size-14' : 'size-[68px]',
          )}
        />
      )}
    </td>
  );
}

/**
 * A folha de um pedido só.
 *
 * @param pedido - O pedido.
 * @returns O cabeçalho, os quatro campos, a caixa de itens, a observação e o total.
 */
function FolhaDetalhada({ pedido }: { pedido: PedidoNaFolha }) {
  const ajustes = ajustesDoPedido(pedido);
  const campos: [string, string][] = [
    ['Solicitante', pedido.solicitante === '' ? '—' : pedido.solicitante],
    ['Data da solicitação', formatarData(pedido.data)],
    ['Canal de origem', pedido.canal === '' ? '—' : pedido.canal],
    ['Fornecedor', fornecedorDaFolha(pedido) ?? '—'],
  ];

  return (
    <>
      <CabecalhoDaFolha
        titulo="Pedido de compra — solicitação de aprovação"
        subtitulo={`${pedido.numero} · ${pedido.casa}`}
      />

      <div className="mb-[26px] grid grid-cols-2 gap-x-6 gap-y-3.5">
        {campos.map(([rotulo, valor]) => (
          <div key={rotulo}>
            <span className="mb-[3px] block font-mono text-[10px] tracking-[0.06em] text-texto-fraco uppercase">
              {rotulo}
            </span>
            <span className="block font-sans text-[13.5px] text-foreground">{valor}</span>
          </div>
        ))}
      </div>

      <div className="mb-5 overflow-hidden rounded-md border border-linha-forte">
        <div className="border-b border-linha bg-superficie-folha px-[18px] py-[13px] font-serif text-[15px] font-medium text-foreground">
          Itens do pedido
        </div>
        <table aria-label="Itens do pedido" className="w-full border-collapse">
          <thead>
            <tr>
              <th className={CLASSES_DO_TH}>
                <span className="sr-only">Foto</span>
              </th>
              <th className={CLASSES_DO_TH}>Descrição</th>
              <th className={cn(CLASSES_DO_TH, 'text-right')}>Qtd.</th>
              <th className={cn(CLASSES_DO_TH, 'text-right')}>Valor unit.</th>
              <th className={cn(CLASSES_DO_TH, 'text-right')}>Total</th>
            </tr>
          </thead>
          <tbody className={cn(CLASSES_DO_CORPO, CLASSES_DO_CORPO_DETALHADO)}>
            {pedido.itens.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-5! text-center text-folha-vazio">
                  Nenhum item adicionado.
                </td>
              </tr>
            ) : (
              pedido.itens.map((item, indice) => {
                const nota = notaDeAjusteDoItem(item);
                return (
                  <tr key={indice}>
                    <CelulaDaFoto item={item} compacto={false} />
                    <td>
                      {item.descricao === '' ? '—' : item.descricao}
                      {item.fornecedor !== null && item.fornecedor !== '' && (
                        <div className={CLASSES_DA_NOTA}>{item.fornecedor}</div>
                      )}
                      {nota !== null && <div className={CLASSES_DA_NOTA}>{nota}</div>}
                    </td>
                    <td className={CLASSES_DO_NUMERO}>{item.quantidade}</td>
                    <td className={CLASSES_DO_NUMERO}>
                      {formatarDinheiro(item.valorUnitario)}
                    </td>
                    <td className={CLASSES_DO_NUMERO}>
                      {formatarDinheiro(item.valorUnitario * item.quantidade)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {ajustes !== null && <BlocoDeAjustes ajustes={ajustes} compacto={false} />}
      </div>

      {pedido.observacao !== null && (
        <div className="mb-5 text-[12.5px] leading-normal text-texto-medio">
          <strong>Observação:</strong> {pedido.observacao}
        </div>
      )}

      <FaixaDoTotal rotulo="TOTAL DO PEDIDO" valor={totalDaFolha(pedido)} />
    </>
  );
}

/**
 * A folha de dois ou mais pedidos, um bloco compacto por pedido.
 *
 * @param pedidos - Os pedidos, na ordem em que saem no papel.
 * @param hora - A hora da geração, `HH:MM`, para o "gerado às".
 * @returns O cabeçalho, um bloco por pedido e o total geral.
 */
function FolhaEmLote({ pedidos, hora }: { pedidos: readonly PedidoNaFolha[]; hora: string }) {
  const quantos = String(pedidos.length);
  const totalGeral = pedidos.reduce((soma, pedido) => soma + totalDaFolha(pedido), 0);

  return (
    <>
      <CabecalhoDaFolha
        titulo="Pedidos de compra — solicitação de aprovação em lote"
        subtitulo={`${quantos} pedidos · gerado às ${hora}`}
      />

      {pedidos.map((pedido, indice) => {
        const fornecedor = fornecedorDaFolha(pedido);
        const ajustes = ajustesDoPedido(pedido);
        return (
          <section
            key={indice}
            aria-label={pedido.numero}
            className="mb-3.5 overflow-hidden rounded-md border border-linha-forte"
          >
            <div className="flex items-baseline justify-between gap-3 border-b border-linha bg-superficie-folha px-3.5 py-[9px] font-sans text-[12.5px] text-foreground">
              <span>
                <strong>{pedido.numero}</strong> —{' '}
                {pedido.solicitante === '' ? '—' : pedido.solicitante} · {pedido.casa}
                {fornecedor !== null && ` · ${fornecedor}`}
              </span>
              <span className="font-mono whitespace-nowrap text-brass-light">
                {formatarDinheiro(totalDaFolha(pedido))}
              </span>
            </div>
            <table aria-label={`Itens de ${pedido.numero}`} className="w-full border-collapse">
              <tbody className={cn(CLASSES_DO_CORPO, CLASSES_DO_CORPO_COMPACTO)}>
                {pedido.itens.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-folha-vazio">
                      Sem itens
                    </td>
                  </tr>
                ) : (
                  pedido.itens.map((item, posicao) => {
                    const nota = notaDeAjusteDoItem(item);
                    return (
                      <tr key={posicao}>
                        <CelulaDaFoto item={item} compacto />
                        <td>
                          {item.descricao === '' ? '—' : item.descricao}
                          {nota !== null && <div className={CLASSES_DA_NOTA}>{nota}</div>}
                        </td>
                        <td className={CLASSES_DO_NUMERO}>
                          {`${String(item.quantidade)}x ${formatarDinheiro(item.valorUnitario)}`}
                        </td>
                        <td className={CLASSES_DO_NUMERO}>
                          {formatarDinheiro(item.valorUnitario * item.quantidade)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            {ajustes !== null && <BlocoDeAjustes ajustes={ajustes} compacto />}
            {pedido.observacao !== null && (
              <div className="border-t border-linha bg-folha-observacao px-3.5 py-2 text-[11.5px] leading-[1.4] text-texto-medio">
                <strong>Observação:</strong> {pedido.observacao}
              </div>
            )}
          </section>
        );
      })}

      <FaixaDoTotal rotulo={`TOTAL GERAL (${quantos} pedidos)`} valor={totalGeral} />
    </>
  );
}
